import { NotificationsService } from '../notifications/notifications.service';
import { StorageService } from '../storage/storage.service';
import { Injectable } from '@angular/core';
import { Platform, AlertController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';

const WIFI_WARNING_DISMISSED_KEY = 'wifiWarningDismissed';
const WIFI_TOAST_SHOWN_KEY = 'wifiToastShownOnce';

export type ConnectionType = 'wifi' | 'cellular' | 'none' | 'unknown';

/**
 * ID: bh-network-service
 * Name: BH Network Service
 * Description: Service used for monitoring network state (online, offline, wifi, cellular).
 * Exposes global isOnline and isWifi; shows one-time WiFi alert on devices when not on WiFi.
 * Version: 2
 *
 * ==============================
 * Change Log
 * ==============================
 * 2021-07-02 - MW - v1: Initial dev
 * 2025-02-04 - v2: WiFi check on devices, global state, one-time notification on dismiss
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  /** Global: whether the device has any network connection */
  isOnline: BehaviorSubject<boolean> = new BehaviorSubject(true);
  /** Global: whether the connection is WiFi (only meaningful on native; on web unknown is treated as true) */
  isWifi: BehaviorSubject<boolean> = new BehaviorSubject(true);
  /** Global: connection type from Capacitor or 'unknown' on web */
  connectionType: BehaviorSubject<ConnectionType> = new BehaviorSubject<ConnectionType>('unknown');

  private wifiAlert: HTMLIonAlertElement | null = null;
  private offlineAlert: HTMLIonAlertElement | null = null;
  private statusListener: { remove: () => Promise<void> } | null = null;

  constructor(
    public platform: Platform,
    public alertController: AlertController,
    public notificationsService: NotificationsService,
    private storageService: StorageService
  ) {
    this.initNetworkMonitoring();
  }

  private async initNetworkMonitoring() {
    await this.platform.ready();
    if (Capacitor.isNativePlatform()) {
      const status = await Network.getStatus();
      this.updateStateFromStatus(status.connected, status.connectionType as ConnectionType);
      this.statusListener = await Network.addListener('networkStatusChange', (status) => {
        this.updateStateFromStatus(status.connected, status.connectionType as ConnectionType);
      });
    } else {
      // Web: use navigator.onLine; we cannot detect WiFi vs cellular
      const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
      this.isOnline.next(online);
      this.isWifi.next(true); // assume OK on web
      this.connectionType.next('unknown');
      if (typeof window !== 'undefined') {
        window.addEventListener('online', () => {
          this.isOnline.next(true);
          this.isWifi.next(true);
          this.connectionType.next('unknown');
        });
        window.addEventListener('offline', () => {
          this.isOnline.next(false);
          this.isWifi.next(false);
          this.connectionType.next('none');
        });
      }
    }
  }

  private updateStateFromStatus(connected: boolean, type: ConnectionType) {
    this.isOnline.next(connected);
    const wifi = connected && type === 'wifi';
    this.isWifi.next(wifi);
    this.connectionType.next(connected ? type : 'none');
  }

  /** Only on native: run one-time WiFi check and show alert if not on WiFi (and not already dismissed). */
  async checkWifiAndMaybeShowAlert(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const dismissed = await this.storageService.getData(WIFI_WARNING_DISMISSED_KEY);
    if (dismissed === true) {
      return;
    }
    const status = await Network.getStatus();
    if (!status.connected || status.connectionType === 'wifi') {
      return;
    }
    await this.presentNotWifiAlert();
  }

  private async presentNotWifiAlert(): Promise<void> {
    if (this.wifiAlert) {
      return;
    }
    this.wifiAlert = await this.alertController.create({
      header: 'Not on Wi‑Fi',
      message: 'You\'re using cellular data. For the best experience, connect to Wi‑Fi when you can.',
      backdropDismiss: true,
      buttons: [
        {
          text: 'OK',
          role: 'cancel',
          handler: () => this.onWifiAlertDismissed()
        }
      ]
    });
    this.wifiAlert.onDidDismiss().then(() => {
      this.onWifiAlertDismissed();
    });
    await this.wifiAlert.present();
  }

  private async onWifiAlertDismissed(): Promise<void> {
    if (!this.wifiAlert) {
      return;
    }
    this.wifiAlert = null;
    await this.storageService.saveData(WIFI_WARNING_DISMISSED_KEY, true);
    const toastShown = await this.storageService.getData(WIFI_TOAST_SHOWN_KEY);
    if (toastShown !== true) {
      this.notificationsService.showToast('Using cellular data. Connect to Wi‑Fi when you can for the best experience.');
      await this.storageService.saveData(WIFI_TOAST_SHOWN_KEY, true);
    }
  }

  public getNetworkType(): string {
    return this.connectionType.getValue();
  }

  public getNetworkStatus(): boolean {
    return this.isOnline.getValue();
  }

  public getIsWifi(): boolean {
    return this.isWifi.getValue();
  }

  public async presentNetworkOfflineAlert() {
    if (!this.offlineAlert) {
      this.offlineAlert = await this.alertController.create({
        header: 'Your device disconnected from the network.',
        message: 'Attempting to reconnect...',
        backdropDismiss: false,
      });
      await this.offlineAlert.present();
    }
  }

  public async dismissNetworkOfflineAlert() {
    if (this.offlineAlert) {
      await this.offlineAlert.dismiss();
      this.offlineAlert = null;
      this.notificationsService.showToast('Your device reconnected to the network.');
    }
  }

  /** Call when destroying the app / service to remove listeners. */
  async destroy() {
    if (this.statusListener) {
      await this.statusListener.remove();
      this.statusListener = null;
    }
  }
}
